/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks\useCustomerCodeData.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Class of Services module.
────────────────────────────────────────────────────────────────*/


import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { fetchCustomerCodeById } from '../api';
import { getDefaultFormState } from '../utils';
import type { FormState } from '../types';

interface UseCustomerCodeDataParams {
  id?: string;
  duplicateId?: string | null;
  tenantSlug?: string;
  isDuplicateMode: boolean;
}

export function useCustomerCodeData({
  id,
  duplicateId,
  tenantSlug,
  isDuplicateMode
}: UseCustomerCodeDataParams) {
  const location = useLocation();
  const { t } = useI18n();
  const { showError } = useNotifications();
  const [formData, setFormData] = useState<FormState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const lastLoadedIdRef = useRef<string | null>(null);
  const lastLocationKeyRef = useRef<string>('');
  const showErrorRef = useRef(showError);

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  const loadCustomerCode = useCallback(async (loadId: string) => {
    if (!tenantSlug) return;

    setIsLoading(true);
    try {
      const record = await fetchCustomerCodeById(loadId, tenantSlug);

      const loaded: FormState = {
        code: record.code ?? '',
        description: record.description ?? '',
        active: record.active ?? true
      };

      setFormData(isDuplicateMode ? { ...loaded, code: '' } : loaded);
    } catch (error) {
      showErrorRef.current(t('customerCodes.messages.loadError'));
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [isDuplicateMode, t, tenantSlug]);

  useEffect(() => {
    const loadId = isDuplicateMode ? duplicateId : id;
    const savedDataKey = 'customerCodes:saveAndNewData';
    const locationKey = location.key || location.pathname + location.search;

    if (!loadId || loadId === 'new') {
      // Check if we need to reset:
      // 1. ID changed
      // 2. Location key changed (React Router navigation)
      // 3. sessionStorage timestamp indicates reset needed
      const resetTimestamp = sessionStorage.getItem(savedDataKey + ':reset');
      const lastResetTimestamp = lastLoadedIdRef.current === 'new' ? 
        sessionStorage.getItem(savedDataKey + ':lastReset') : null;
      
      const shouldReset = lastLoadedIdRef.current !== 'new' || 
                          lastLocationKeyRef.current !== locationKey ||
                          (lastLoadedIdRef.current === 'new' && loadId === 'new' && 
                           resetTimestamp && resetTimestamp !== lastResetTimestamp);
      
      if (shouldReset) {
        lastLoadedIdRef.current = 'new';
        lastLocationKeyRef.current = locationKey;
        
        // Clear sessionStorage for "Save and New" functionality
        try {
          sessionStorage.removeItem(savedDataKey);
          if (resetTimestamp) {
            sessionStorage.setItem(savedDataKey + ':lastReset', resetTimestamp);
            sessionStorage.removeItem(savedDataKey + ':reset');
          }
        } catch (error) {
          // Ignore errors when clearing
        }
        
        // Reset form to default values
        if (!isDuplicateMode) {
          setFormData(getDefaultFormState());
        }
      }
      return;
    }

    // Prevent re-fetching if we already loaded this ID
    if (lastLoadedIdRef.current === loadId && lastLocationKeyRef.current === locationKey) {
      return;
    }

    lastLoadedIdRef.current = loadId;
    lastLocationKeyRef.current = locationKey;

    loadCustomerCode(loadId).catch(() => {
      lastLoadedIdRef.current = null;
      setFormData(null);
    });
  }, [duplicateId, id, isDuplicateMode, loadCustomerCode, location.key, location.pathname, location.search]);

  return {
    formData,
    isLoading
  };
}

